// Script d'initialisation des données Firebase pour CETROBON
import { 
  collection, 
  doc, 
  setDoc, 
  serverTimestamp,
  getDocs,
  query,
  where
} from 'firebase/firestore';
import { 
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  updateProfile
} from 'firebase/auth';
import { auth, db } from './config';

// Données initiales pour CETROBON
const initializeFirebaseData = async () => {
  try {
    console.log('🔥 Initialisation des données Firebase CETROBON...');

    // 1. Créer l'utilisateur admin KILA France
    console.log('👤 Création de l\'admin KILA France...');
    
    let adminUser;
    try {
      // Essayer de créer l'admin
      const adminCredential = await createUserWithEmailAndPassword(
        auth, 
        'contact@kila-france.com', 
        'b12As5o3**!!2024'
      );
      adminUser = adminCredential.user;
      
      // Mettre à jour le profil
      await updateProfile(adminUser, {
        displayName: 'Admin KILA France'
      });
      
      console.log('✅ Admin KILA France créé avec succès');
    } catch (error) {
      if (error.code === 'auth/email-already-in-use') {
        // L'admin existe déjà, se connecter
        const adminCredential = await signInWithEmailAndPassword(
          auth, 
          'contact@kila-france.com', 
          'b12As5o3**!!2024'
        );
        adminUser = adminCredential.user;
        console.log('✅ Admin KILA France existe déjà');
      } else {
        throw error;
      }
    }

    // Sauvegarder les données admin dans Firestore
    await setDoc(doc(db, 'users', adminUser.uid), {
      name: 'Admin KILA France',
      email: 'contact@kila-france.com',
      role: 'admin',
      createdAt: serverTimestamp(),
      isActive: true
    });

    // 2. Créer les plans d'abonnement
    console.log('💳 Création des plans d\'abonnement...');
    
    const subscriptionPlans = [
      {
        id: 'plan-gratuit',
        name: 'Plan Gratuit',
        description: 'Parfait pour débuter',
        price_monthly: 0,
        commission_rate: 0.20,
        features: [
          'Accès aux ateliers de base',
          'Support communautaire',
          'Commission 20%'
        ],
        is_active: true,
        created_at: serverTimestamp()
      },
      {
        id: 'plan-standard',
        name: 'Plan Standard',
        description: 'Pour les passionnés',
        price_monthly: 49,
        commission_rate: 0.10,
        features: [
          'Accès à tous les ateliers',
          'Support prioritaire',
          'Commission 10%',
          'Réservation prioritaire'
        ],
        is_active: true,
        created_at: serverTimestamp()
      },
      {
        id: 'plan-premium',
        name: 'Plan Premium',
        description: 'Pour les professionnels',
        price_monthly: 99,
        commission_rate: 0.0,
        features: [
          'Accès illimité',
          'Support VIP',
          'Commission 0%',
          'Ateliers exclusifs',
          'Certification'
        ],
        is_active: true,
        created_at: serverTimestamp()
      }
    ];

    for (const plan of subscriptionPlans) {
      await setDoc(doc(db, 'subscription-plans', plan.id), plan);
    }
    console.log('✅ Plans d\'abonnement créés');

    // 3. Créer quelques codes promo de test
    console.log('🎫 Création des codes promo...');
    
    const promoCodes = [
      {
        id: 'welcome2024',
        code: 'WELCOME2024',
        description: 'Code de bienvenue 2024',
        discount_percentage: 20,
        discount_amount: null,
        max_uses: 100,
        used_count: 0,
        valid_from: new Date('2024-01-01'),
        valid_until: new Date('2024-12-31'),
        applicable_plans: ['plan-standard', 'plan-premium'],
        is_active: true,
        created_at: serverTimestamp()
      },
      {
        id: 'kila50',
        code: 'KILA50',
        description: 'Réduction KILA France',
        discount_percentage: null,
        discount_amount: 50,
        max_uses: 50,
        used_count: 0,
        valid_from: new Date('2024-01-01'),
        valid_until: new Date('2024-06-30'),
        applicable_plans: ['plan-premium'],
        is_active: true,
        created_at: serverTimestamp()
      }
    ];

    for (const promo of promoCodes) {
      await setDoc(doc(db, 'promo-codes', promo.id), promo);
    }
    console.log('✅ Codes promo créés');

    // 4. Créer des chefs/formateurs de test
    console.log('👨‍🍳 Création des chefs...');
    
    const chefs = [
      {
        id: 'chef-1',
        name: 'Chef Marie Dubois',
        email: 'marie.dubois@example.com',
        specialty: 'Pâtisserie française',
        experience: '15 ans d\'expérience',
        rating: 4.8,
        location: 'Paris',
        bio: 'Ancienne cheffe pâtissière du Ritz, Marie partage sa passion pour la pâtisserie française traditionnelle.',
        avatar: 'https://images.unsplash.com/photo-1559166631-c3e6c4c9c5b1?w=400',
        is_active: true,
        created_at: serverTimestamp()
      },
      {
        id: 'chef-2',
        name: 'Chef Antoine Moreau',
        email: 'antoine.moreau@example.com',
        specialty: 'Cuisine méditerranéenne',
        experience: '12 ans d\'expérience',
        rating: 4.6,
        location: 'Lyon',
        bio: 'Spécialiste de la cuisine méditerranéenne, Antoine vous fera découvrir les saveurs du soleil.',
        avatar: 'https://images.unsplash.com/photo-1566554273541-37a9ca77b91b?w=400',
        is_active: true,
        created_at: serverTimestamp()
      }
    ];

    for (const chef of chefs) {
      await setDoc(doc(db, 'chefs', chef.id), chef);
    }
    console.log('✅ Chefs créés');

    // 5. Créer des ateliers de test
    console.log('🍳 Création des ateliers...');
    
    const workshops = [
      {
        id: 'atelier-1',
        title: 'Macarons parisiens',
        description: 'Apprenez à réaliser de parfaits macarons français avec Chef Marie.',
        category: 'Pâtisserie',
        chef_id: 'chef-1',
        chef_name: 'Chef Marie Dubois',
        price: 85,
        duration: '3 heures',
        max_participants: 8,
        location: 'Paris 6ème',
        difficulty: 'Intermédiaire',
        image: 'https://images.unsplash.com/photo-1569864358642-9d1684040f43?w=800',
        includes: [
          'Tous les ingrédients',
          'Matériel professionnel',
          'Livret de recettes',
          'Dégustation'
        ],
        next_sessions: [
          '2024-02-15T14:00:00',
          '2024-02-22T14:00:00',
          '2024-03-01T14:00:00'
        ],
        is_active: true,
        created_at: serverTimestamp()
      },
      {
        id: 'atelier-2',
        title: 'Cuisine méditerranéenne',
        description: 'Découvrez les secrets de la cuisine du soleil avec Chef Antoine.',
        category: 'Cuisine du monde',
        chef_id: 'chef-2',
        chef_name: 'Chef Antoine Moreau',
        price: 75,
        duration: '2h30',
        max_participants: 10,
        location: 'Lyon 2ème',
        difficulty: 'Débutant',
        image: 'https://images.unsplash.com/photo-1565299624946-b28f40a0ca4b?w=800',
        includes: [
          'Produits frais',
          'Huile d\'olive premium',
          'Recettes traditionnelles',
          'Repas complet'
        ],
        next_sessions: [
          '2024-02-20T18:30:00',
          '2024-02-27T18:30:00',
          '2024-03-05T18:30:00'
        ],
        is_active: true,
        created_at: serverTimestamp()
      }
    ];

    for (const workshop of workshops) {
      await setDoc(doc(db, 'workshops', workshop.id), workshop);
    }
    console.log('✅ Ateliers créés');

    console.log('🎉 Initialisation Firebase terminée avec succès !');
    
    return {
      success: true,
      message: 'Données Firebase initialisées',
      admin: {
        email: 'contact@kila-france.com',
        password: 'b12As5o3**!!2024',
        name: 'Admin KILA France'
      }
    };

  } catch (error) {
    console.error('❌ Erreur lors de l\'initialisation Firebase:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

// Fonction pour vérifier si les données existent
const checkFirebaseData = async () => {
  try {
    // Vérifier si l'admin existe
    const adminQuery = query(
      collection(db, 'users'), 
      where('email', '==', 'contact@kila-france.com')
    );
    const adminSnapshot = await getDocs(adminQuery);
    
    // Vérifier les plans
    const plansSnapshot = await getDocs(collection(db, 'subscription-plans'));
    
    // Vérifier les ateliers
    const workshopsSnapshot = await getDocs(collection(db, 'workshops'));
    
    return {
      adminExists: !adminSnapshot.empty,
      plansCount: plansSnapshot.size,
      workshopsCount: workshopsSnapshot.size
    };
  } catch (error) {
    console.error('Erreur lors de la vérification:', error);
    return null;
  }
};

export { initializeFirebaseData, checkFirebaseData };